import React, { useState } from 'react'
import { FiMove } from 'react-icons/fi'
import { CgPen, CgMoreAlt, CgCornerUpLeft } from 'react-icons/cg'
import tw, { styled } from 'twin.macro'
import { useParams, useNavigate } from 'react-router-dom'
import * as Popover from '@radix-ui/react-popover'
import { Tooltip } from 'common/components/Tooltip/Tooltip'
import {
  useAddExtraWorkoutToDayMutation,
  useDeleteMultiWorkoutMutation,
  useDeleteWktOfMultiWorkoutMutation,
  useDeleteWorkoutMutation,
  useLazyGetWorkoutsByIdsQuery,
  useReorderProgramMutation,
  useUpdateMultiWktDaysMutation,
  useUpdateWorkoutMutation,
} from '../programApi'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { maybePluralize } from 'common/utils/stringUtils'
import { clipboardCounterId, clipboardTextId } from '../constants/cssSelectorConstants'
import { DayHeaderContainer } from './styles'
import { useDispatch, useSelector } from 'react-redux'
import {
  getWorkoutsCopied,
  workoutsCopied,
  getDayIdxIsEmpty,
  getProgramMultiWktDays,
  getProgramMultiWktDaysCopied,
} from '../programSlice'
import { Spinner } from 'common/components/Spinner/Spinner'
import { handlePasteWorkout } from '../utils/pasteWorkoutUtils'
import { getNewWorkout } from '../programModels'
import { updateTabRowsOnDelete } from 'modules/Layout/utils/layoutUtils'
import useQuizResultsExists from 'modules/AppOnboardingQuiz/hooks/useQuizResultsExists'
import { Dialog, DialogContent, DialogTrigger } from 'common/components/Dialog/Dialog'
import { CreateDialogBanner } from 'common/components/CreateDialogBanner/CreateDialogBanner'
import { NEW_USER_QUIZ } from 'modules/App/components/Header'
import { setIdsOfResultsToDelete, setTitleOfContentToDelete } from 'modules/AppOnboardingQuiz/quizFlowSlice'
import { removeItemsInCollections } from 'modules/Collections/collectionUtils'
import WorkoutDuration from './WorkoutDuration'
import { getOrdinalSuffix } from 'common/utils/numberUtils'
import { DeleteDialogBanner } from 'common/components/DeleteDialogBanner/DeleteDialogBanner'
import { Skeleton } from 'common/components/Skeleton/Skeleton'
import { updateManyWorkoutContentTags } from '../utils/contentTagUtils'

const HeaderActions = styled.div(({ isEditing, isPopoverOpen }) => [
  tw`hidden items-center py-1 px-1.5 absolute z-10 top-1 right-1
  rounded-md bg-white border-[1px] border-gray-100 shadow group-hover:flex space-x-1`,
  (isEditing || isPopoverOpen) && tw`flex`,
])

const popoverBtnCss = tw`
  flex items-center bg-white hover:bg-gray-300 hover:bg-opacity-30
  text-sm text-tBlack transition-all rounded-md justify-start
  font-medium px-2 py-2 w-full`

export function DayHeader(props) {
  const {
    workout,
    setEditIdx,
    dayIdx,
    isEditing,
    orgId,
    setModalWorkout,
    setEditWorkoutInfoModalOpen,
    switchToMultiWkts,
    workoutIds = [],
    mainWktId,
    selectedWktId,
    setSelectedWktId,
  } = props
  const { attributes, listeners } = props.dragHandleProps

  const dispatch = useDispatch()
  const workoutsCopiedState = useSelector(getWorkoutsCopied)
  const wktIdsSorted = useSelector((state) => state.program.wktIdsSorted)
  const dayIdxIsEmpty = useSelector(getDayIdxIsEmpty)
  const maxDayIdx = useSelector((state) => state.program.maxDayIdx)
  const multiWktDays = useSelector(getProgramMultiWktDays)
  const multiWktDaysCopied = useSelector(getProgramMultiWktDaysCopied)
  const [updateWorkout] = useUpdateWorkoutMutation()
  const [updateMultiWktDays] = useUpdateMultiWktDaysMutation()
  const [reorderProgram] = useReorderProgramMutation()
  const [addExtraWktToDay] = useAddExtraWorkoutToDayMutation()
  const [getWorkoutsByIds] = useLazyGetWorkoutsByIdsQuery()

  const programIsLoading = false //TO-DO: temp, do later

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const { id: programId } = useParams()
  const navigate = useNavigate()
  const { createAlert } = useAlert()

  let itemIds = { [workout.id]: true }
  workoutIds.forEach((wktId) => (itemIds[wktId] = true))
  const { quizResultExists, isQuizResultsLoading } = useQuizResultsExists({
    coachOrgId: orgId,
    itemIds,
  })

  let innerContent
  if (workout && programIsLoading) {
    innerContent = renderSpinner()
  } else if (workout && !programIsLoading && !isQuizResultsLoading) {
    innerContent = renderHeaderActions()
  } else {
    innerContent = <div className='w-0' />
  }

  const numMultiWkts = workout?.multiWktDayId ? multiWktDays?.[workout.multiWktDayId]?.workouts?.length || 0 : 0

  return (
    <DayHeaderTitle
      dayIdx={dayIdx}
      multiWktIdx={workout?.multiWktIdx}
      numMultiWkts={numMultiWkts}
      switchToMultiWkts={switchToMultiWkts}
      isEditing={isEditing}
    >
      {innerContent}
      <div className='mt-1'>
        {workout?.type !== 'empty' ? (
          <WorkoutDuration
            workout={workout}
            orgId={orgId}
            selectedWktId={selectedWktId}
            multiWtkIds={multiWktDays?.[workout.multiWktDayId]?.workouts || []}
          />
        ) : (
          <div className='h-[26px]' />
        )}
      </div>
    </DayHeaderTitle>
  )

  function renderSpinner() {
    return (
      <div className='hidden items-center py-0.5 absolute right-2 bg-gray-50 group-hover:flex'>
        <Spinner />
      </div>
    )
  }

  function renderHeaderActions() {
    return (
      <HeaderActions isEditing={isEditing} isPopoverOpen={isPopoverOpen}>
        {/* dont show for empty workouts and parent multi workouts */}
        {(workout.type === 'workout' || workout.type === 'single_video') && (!workoutIds.length || selectedWktId) ? (
          <Tooltip content='Edit details' triggerClasses='flex !mr-0.5'>
            <button
              aria-label='Edit details'
              className='flex'
              onClick={() => {
                setModalWorkout(workout)
                setEditWorkoutInfoModalOpen(true)
              }}
            >
              <CgPen className='cursor-pointer w-3.5 h-3.5 text-gray-500 hover:text-tGreen opacity-100' />
            </button>
          </Tooltip>
        ) : null}
        <Tooltip content='Drag'>
          <FiMove
            className='cursor-move w-3.5 h-3.5 text-gray-500 hover:text-tGreen'
            {...attributes}
            {...listeners}
            tabIndex={-1}
          />
        </Tooltip>
        <Popover.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <Popover.Trigger
            aria-label='Workout actions'
            onClick={(e) => {
              e.stopPropagation()
              setIsPopoverOpen(true)
            }}
            tabIndex={-1}
          >
            <CgMoreAlt className='cursor-pointer w-5 h-5 text-gray-500 hover:text-tGreen' />
          </Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              className='bg-white px-2 py-2 rounded-md shadow-xl border-2 border-gray-300 z-50'
              align='end'
              alignOffset={-7}
              sideOffset={5}
              arrowPadding={12}
            >
              <Popover.Arrow className='fill-gray-300' />
              {(workout.type !== 'empty' || workoutIds.length > 1) && (
                <Popover.Close asChild={true}>
                  <button css={popoverBtnCss} onClick={(e) => handleCopy(e)}>
                    Copy workout
                  </button>
                </Popover.Close>
              )}
              {workoutsCopiedState.length > 0 && (
                <Popover.Close asChild={true}>
                  <button
                    css={popoverBtnCss}
                    onClick={(e) => {
                      handleDuplicateMouseLeave()
                      handlePaste(e)
                    }}
                    onMouseEnter={handleDuplicateMouseEnter}
                    onMouseLeave={handleDuplicateMouseLeave}
                  >
                    {maybePluralize({ text: 'Paste workout', count: workoutsCopiedState.length })}
                  </button>
                </Popover.Close>
              )}
              <Popover.Close asChild={true}>
                <button css={popoverBtnCss} onClick={(e) => handleAddExtraWorkout(e)}>
                  Add {getOrdinalSuffix(Math.max(workoutIds.length + 1, 2))} workout
                </button>
              </Popover.Close>
              <Popover.Close asChild={true}>
                <button css={popoverBtnCss} onClick={() => navigate(`workouts/${props.workout.id}`)}>
                  Expand workout
                </button>
              </Popover.Close>
              <Popover.Close asChild={true}>
                <button css={popoverBtnCss} onClick={(e) => handleInsertToRight(e)}>
                  Insert day
                </button>
              </Popover.Close>
              <DeleteWktButton
                quizResultExists={quizResultExists}
                orgId={orgId}
                dayIdx={dayIdx}
                setEditIdx={setEditIdx}
                workout={workout}
                workoutIds={workoutIds}
                mainWktId={mainWktId}
                selectedWktId={selectedWktId}
                setSelectedWktId={setSelectedWktId}
              />
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </HeaderActions>
    )
  }

  async function handleCopy(e) {
    e.stopPropagation()
    let multiWktsCopied = {}
    let multiWktIds = []
    if (!selectedWktId && workout?.multiWktDayId) {
      const wktIds = multiWktDays?.[workout.multiWktDayId]?.workouts || []
      multiWktIds = [...multiWktIds, ...wktIds]
    }
    const { data } = await getWorkoutsByIds({ orgId, wktIds: multiWktIds })
    multiWktsCopied = data

    const multiWktDaysCopied =
      selectedWktId || !workout?.multiWktDayId ? {} : { [workout.multiWktDayId]: multiWktDays[workout.multiWktDayId] }
    dispatch(
      workoutsCopied({
        workouts: [
          {
            ...workout,
            // Ensures that only a single workout is copied when a child workout
            // of a multiWkt is selected, instead of copying the entire multiWkt.
            multiWktDayId: selectedWktId ? null : workout?.multiWktDayId || null,
            multiWktIdx: selectedWktId ? null : (workout?.multiWktIdx ?? null),
          },
        ],
        multiWktsCopied: selectedWktId || !workout?.multiWktDayId ? {} : multiWktsCopied,
        multiWktDaysCopied,
      })
    )
    createAlert({ text: 'Workout copied!', type: 'success' })
  }

  async function handlePaste(e) {
    e.stopPropagation()
    if (selectedWktId && workout?.type === 'empty' && workoutsCopiedState.length === 1) {
      // pasting on empty selected child wkt of multiWkt
      const copiedWkt = workoutsCopiedState[0]
      const updatedWkt = {
        ...copiedWkt,
        id: workout?.id,
        dayIdx: workout?.dayIdx || null,
        multiWktDayId: workout?.multiWktDayId || null,
        multiWktIdx: workout?.multiWktIdx ?? null,
      }
      await updateWorkout({ orgId, workoutId: workout?.id, workout: updatedWkt })
      await updateManyWorkoutContentTags({
        orgId,
        workouts: { [updatedWkt.id]: updatedWkt },
        programId,
        updateType: 'add',
      })
      const workoutText = maybePluralize({ text: 'Workout', count: workoutsCopied.length })
      createAlert({ text: `${workoutText} pasted!`, type: 'success' })
    } else {
      let multiWktIds = []
      let multiWktDaysUpdated = { ...multiWktDaysCopied, ...multiWktDays }
      workoutsCopiedState.forEach((wkt) => {
        if (wkt?.multiWktDayId) {
          const wktIds = multiWktDaysUpdated?.[wkt.multiWktDayId]?.workouts || []
          multiWktIds = [...multiWktIds, ...wktIds]
        }
      })
      handlePasteWorkout({
        pastedAtDayIdx: dayIdx,
        workoutsCopied: workoutsCopiedState,
        wktIdsSorted,
        multiWktIds,
        multiWktDays: multiWktDaysUpdated,
        dispatch,
        orgId,
        dayIdxIsEmpty,
        maxDayIdx,
        reorderProgram,
        updateMultiWktDays,
        getWorkoutsByIds,
        programId,
        createAlert,
      })
    }
  }

  function handleInsertToRight(e) {
    e.stopPropagation()

    handlePasteWorkout({
      pastedAtDayIdx: dayIdx,
      workoutsCopied: [getNewWorkout(dayIdx)],
      wktIdsSorted,
      multiWktIds: workoutIds,
      multiWktDays,
      dispatch,
      orgId,
      dayIdxIsEmpty,
      maxDayIdx,
      reorderProgram,
      updateMultiWktDays,
      getWorkoutsByIds,
      programId,
      createAlert,
      actionText: 'inserted',
    })
  }

  function handleAddExtraWorkout(e) {
    e.stopPropagation()
    addExtraWktToDay({ orgId, programId, dayIdx, multiWktDayId: workout?.multiWktDayId, mainWktId })
    if (switchToMultiWkts) {
      switchToMultiWkts()
    }
    createAlert({ text: `Workout added!`, type: 'success' })
  }

  function handleDuplicateMouseEnter() {
    document.getElementById(clipboardTextId['workout']).classList.add('!text-tGreen')
    document.getElementById(clipboardCounterId['workout']).classList.add('!bg-tGreen', '!text-white')
  }

  function handleDuplicateMouseLeave() {
    document.getElementById(clipboardTextId['workout']).classList.remove('!text-tGreen')
    document.getElementById(clipboardCounterId['workout']).classList.remove('!bg-tGreen', '!text-white')
  }
}

function DeleteWktButton({
  quizResultExists,
  orgId,
  dayIdx,
  setEditIdx,
  workout,
  workoutIds = [],
  mainWktId,
  selectedWktId,
  setSelectedWktId,
}) {
  const { id: programId } = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [deleteWorkout] = useDeleteWorkoutMutation()
  const [deleteMultiWorkout] = useDeleteMultiWorkoutMutation()
  const [deleteWktOfMultiWorkout] = useDeleteWktOfMultiWorkoutMutation()

  const [isDeletingWkt, setIsDeletingWkt] = useState(false)
  const shouldDeleteMultiWkt = Boolean(workoutIds.length) && !selectedWktId

  const handleDelete = async () => {
    setIsDeletingWkt(true)
    // deleting multiWkt
    if (shouldDeleteMultiWkt) {
      await deleteMultiWorkout({
        orgId,
        programId,
        dayIdx,
        multiWktDayId: workout?.multiWktDayId,
        workoutIds,
        mainWktId,
      })
      await updateTabRowsOnDelete({ coachOrgId: orgId, itemIds: workoutIds })
      await removeItemsInCollections({ coachOrgId: orgId, itemIds: workoutIds })
    } else if (selectedWktId) {
      await deleteWktOfMultiWorkout({
        orgId,
        programId,
        workout,
        workoutIds,
        mainWktId,
      })
      await updateTabRowsOnDelete({ coachOrgId: orgId, itemIds: [selectedWktId] })
      await removeItemsInCollections({ coachOrgId: orgId, itemIds: [selectedWktId] })
    } else {
      const workoutId = workout?.id
      await deleteWorkout({
        orgId,
        workoutId: workout.id,
        programId,
        workout,
        dayIdx,
      })
      await updateTabRowsOnDelete({ coachOrgId: orgId, itemIds: [workoutId] })
      await removeItemsInCollections({ coachOrgId: orgId, itemIds: [workoutId] })
    }

    setEditIdx(null)
    if (setSelectedWktId) {
      setSelectedWktId(null)
    }
    setIsDeletingWkt(false)
  }

  let resultsToDelete = { [workout.id]: true }
  workoutIds.forEach((id) => (resultsToDelete[id] = true))

  const idxOfMultiWkt = workoutIds.indexOf(selectedWktId)
  const deleteWktText =
    workoutIds.length > 1 && selectedWktId
      ? `Delete workout ${idxOfMultiWkt + 1} of ${workoutIds.length}`
      : 'Delete workout'

  if (quizResultExists) {
    return (
      <Dialog>
        <DialogTrigger css={popoverBtnCss} onOpenCb={(e) => e.stopPropagation()}>
          {deleteWktText}
        </DialogTrigger>
        <DialogContent>
          <CreateDialogBanner
            titleText={null}
            createBtnText='Go to quiz'
            text='This workout cannot be deleted because it is used as a result in the new user quiz. To delete the workout, please remove it from the quiz first.'
            handleCreate={() => {
              dispatch(setIdsOfResultsToDelete({ itemIds: resultsToDelete }))
              dispatch(setTitleOfContentToDelete(workout.title || 'Untitled Workout'))
              navigate(NEW_USER_QUIZ)
            }}
          />
        </DialogContent>
      </Dialog>
    )
  }

  // Show delete and allow deleting if multiwkt
  if (workout.type !== 'empty' || shouldDeleteMultiWkt) {
    return (
      <Dialog>
        <DialogTrigger css={popoverBtnCss} onOpenCb={(e) => e.stopPropagation()} disabled={isDeletingWkt}>
          {isDeletingWkt && <Spinner />}
          <span>{deleteWktText}</span>
        </DialogTrigger>
        <DialogContent>
          <DeleteDialogBanner
            text={
              shouldDeleteMultiWkt ? 'This will delete all workouts for this day.' : 'This will delete the workout.'
            }
            handleDelete={handleDelete}
            loading={isDeletingWkt}
          />
        </DialogContent>
      </Dialog>
    )
  }

  return null
}

const Title = tw.div`
  text-tBlack
  text-sm
  leading-none
  font-bold
`

export function DayHeaderTitle(props) {
  const {
    dayIdx,
    containerClasses,
    titleText,
    titleClasses,
    switchToMultiWkts,
    isEditing,
    children,
    multiWktIdx,
    numMultiWkts,
  } = props

  const { workoutId } = useParams()
  const isFromLargeDayView = workoutId ? true : false

  let dayHeaderText
  if (titleText) {
    dayHeaderText = titleText
  } else if (isFromLargeDayView) {
    const weekNum = Math.floor(dayIdx / 7) + 1
    const dayNum = (dayIdx % 7) + 1
    dayHeaderText = `Week ${weekNum >= 0 ? weekNum : ''}, Day ${dayNum >= 0 ? dayNum : ''}`
  } else {
    //is DayView and not first day of week
    dayHeaderText = `Day ${(dayIdx % 7) + 1}`
  }

  return (
    <DayHeaderContainer className={containerClasses}>
      <Title data-testid='dayHeaderTitle' className={titleClasses}>
        {dayIdx >= 0 ? dayHeaderText : <Skeleton className='w-10' />}
      </Title>
      {Boolean(switchToMultiWkts) && (
        <div className='text-xxs text-gray-500 leading-none mt-1'>
          Workout {multiWktIdx + 1} of {numMultiWkts}
        </div>
      )}
      {Boolean(switchToMultiWkts) && (
        <div className='absolute left-1 top-1 z-10' css={isFromLargeDayView && tw`left-2 top-2`}>
          <Tooltip content='Return to all workouts' triggerClasses='flex'>
            <button
              className='flex items-center py-1 px-1.5 rounded bg-white border-[1px] border-gray-100 shadow text-gray-500 hover:text-tGreen animate-drawAttention'
              onClick={() => switchToMultiWkts()}
            >
              <CgCornerUpLeft className='cursor-pointer w-[14px] h-[14px]' css={isFromLargeDayView && tw`w-4 h-4`} />
              {isEditing && (
                <span className='ml-1 text-xxs font-semibold' css={isFromLargeDayView && tw`text-xs`}>
                  All workouts
                </span>
              )}
            </button>
          </Tooltip>
        </div>
      )}
      {children}
    </DayHeaderContainer>
  )
}
